// msalInstance.js

import {
    EventType,
    LogLevel,
    PublicClientApplication
} from '@azure/msal-browser';
// import { debugMSAL } from './Auth-Utils';
// import { msalConfig } from './Auth-Utils';

class MsalInstance {
    public debug(error: any) {
        let showLogs = false;
        if (showLogs) {
            console.error('[debugMSAL]: ', error);
        }
    }
    // Config settings for the PCA - set in index.tsx
    // Information about all of the config options and what they do can be found here
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
    public msalConfig = {
        auth: {
            clientId: `${process.env.REACT_APP_MSAL_CLIENT_ID}`,
            authority: `https://login.onevirtualmarketplace.com/${process.env.REACT_APP_MSAL_TENANT_NAME}.onmicrosoft.com/${process.env.REACT_APP_MSAL_SIGNIN_POLICY}`,
            knownAuthorities: [`login.onevirtualmarketplace.com`],
            postLogoutRedirectUri: `${process.env.REACT_APP_HOST_DOMAIN}`,
            redirectUri: `${process.env.REACT_APP_HOST_DOMAIN}`,
            navigateToLoginRequestUrl: false
        },
        cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false
        },
        system: {
            loggerOptions: {
                loggerCallback: (
                    level: LogLevel,
                    message: string,
                    containsPii: boolean
                ): void => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            this.debug(message);
                            return;
                        case LogLevel.Info:
                            this.debug(message);
                            return;
                        case LogLevel.Verbose:
                            this.debug(message);
                            return;
                        case LogLevel.Warning:
                            this.debug(message);
                            return;
                    }
                },
                piiLoggingEnabled: false
            },
            windowHashTimeout: 60000,
            iframeHashTimeout: 6000,
            loadFrameTimeout: 9000,
            asyncPopups: false
            // If you ever need to test forcing a token to expire
            // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/810
            // tokenRenewalOffsetSeconds: 301
        }
    };

    private static instance: MsalInstance;
    public msalApp: PublicClientApplication;

    public reinstantiateMsal() {
        this.msalApp = new PublicClientApplication(this.msalConfig);
    }

    /**
     * Clean up old MSAL tokens from the previous B2C domain
     * Removes localStorage entries containing the old b2clogin.com domain
     */
    private cleanupOldDomainTokens() {
        try {
            const oldDomain = 'b2clogin.com';
            const keysToRemove: string[] = [];

            // Find all localStorage keys containing the old domain
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.includes(oldDomain)) {
                    keysToRemove.push(key);
                }
            }

            // Remove the old domain keys
            keysToRemove.forEach(key => {
                localStorage.removeItem(key);
                console.info(
                    `[cleanupOldDomainTokens]: Removed old token key: ${key}`
                );
            });

            if (keysToRemove.length > 0) {
                console.info(
                    `[cleanupOldDomainTokens]: Cleaned up ${keysToRemove.length} old domain entries`
                );
            }
        } catch (error) {
            console.error(
                '[cleanupOldDomainTokens]: Error cleaning up old tokens',
                error
            );
        }
    }

    private constructor() {
        // Clean up any old tokens from the previous domain
        this.cleanupOldDomainTokens();

        this.msalApp = new PublicClientApplication(this.msalConfig);

        /*
         *   After you complete a successful password reset, you recieve back a token, but
         *   this token cannot be used for sign-in. So check the tfp field in the payload for the
         *   name of the password reset policy, if you find it, logout and clear the token so the user
         *   won't get an error when trying to sign in after changing their password.
         */
        this.msalApp.addEventCallback((event: any) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                if (
                    event.payload.idTokenClaims['tfp'] ===
                    `${process.env.REACT_APP_MSAL_FORGET_PASSWORD_POLICY}`
                ) {
                    msalInstance.msalApp.logoutRedirect({
                        postLogoutRedirectUri: '/',
                        account: event.payload.account
                    });
                }
            }
            if (event.eventType === EventType.LOGIN_FAILURE) {
                console.error('[setPCA]: msal redirect login', event.payload);
                document.dispatchEvent(
                    new CustomEvent('startUserLogoutSessionOnly')
                );
            }
            if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                // acquireToken() in Auth-Utils already handles this by calling loginRedirect.
                // Do not dispatch startUserLogoutSessionOnly here — doing so fires logoutRedirect
                // 2 seconds after loginRedirect has already started, causing an interaction_in_progress
                // conflict that leaves MSAL in a broken state.
                console.error(
                    '[setPCA]: msal redirect acquire token',
                    event.payload
                );
            }
        });
        this.msalApp.handleRedirectPromise().catch(err => {
            console.error('[setPCA]: handleRedirectPromise error', err);
        });
    }

    public static getInstance(): MsalInstance {
        if (!MsalInstance.instance) {
            MsalInstance.instance = new MsalInstance();
        }
        return MsalInstance.instance;
    }
}

const msalInstance = MsalInstance.getInstance();
Object.freeze(msalInstance);

export default msalInstance;
